import { Dispatch, JSX, SetStateAction, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import ProfileCard from "@/components/cards/ProfileCard.tsx";
import { GateProfilePagesProps, ProfileTabs } from "@/components/gate/profiles/types.ts";
import { IconPlus } from "@/components/icons";

import { Profile } from "@/api/auth/auth.types.ts";
import { cn } from "@/utils/cn.ts";

interface ChooseProfileProps extends GateProfilePagesProps {
  profiles: Profile[];
  profileId?: number;
  setProfileId: Dispatch<SetStateAction<number>>;
  setIsEdit: Dispatch<SetStateAction<boolean>>;
}

const ChooseProfile = ({ profiles, setTab, setProfileId, setIsEdit }: ChooseProfileProps): JSX.Element => {
  const { t } = useTranslation();

  const spanRef = useRef<HTMLSpanElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const userProfiles = profiles?.filter((profile) => !profile.is_kids);

  useEffect(() => {
    if (spanRef.current && buttonRef.current) {
      const buttonHeight = buttonRef.current.clientHeight;
      spanRef.current.style.height = `${buttonHeight}px`;
    }
  }, []);

  return (
    <div
      className={"flex h-full min-h-screen w-full max-w-[51.875rem] flex-col justify-between gap-8 py-20 max-sm:py-6"}
    >
      <span ref={spanRef} className={"max-lg:hidden"} />
      <div className={"flex flex-col items-center"}>
        <h2 className={"mb-6 text-[3.125rem]/[3.75rem] font-semibold max-lg:text-2xl"}>{t("General.who-is-here")}</h2>
        <h3 className={"mb-4 w-full px-3 text-[1.125rem] font-semibold max-sm:p-0 md:hidden"}>{t("adult-profiles")}</h3>
        <nav className={"w-full"}>
          <ul className={"flex flex-wrap  justify-center"}>
            {userProfiles?.map((profile) => (
              <li
                key={profile.id}
                className={cn("w-[33%] p-3 max-md:w-[50%] max-sm:w-[100%] max-sm:px-0", {
                  "max-md:w-full": userProfiles.length == 1,
                })}
              >
                <ProfileCard
                  id={profile.id}
                  name={profile.name}
                  age={profile.age}
                  interface_lang={profile.interface_lang}
                  picture={profile.picture}
                  is_lock={profile.is_lock}
                  setProfileId={setProfileId}
                  setTab={setTab}
                  setIsEdit={setIsEdit}
                />
              </li>
            ))}
            {userProfiles?.length < 3 ? (
              <li className="w-[33%] p-3 max-md:w-[100%] max-sm:px-0">
                <button
                  onClick={() => setTab(ProfileTabs.ADD_PROFILE)}
                  type={"button"}
                  className={cn(
                    "group flex aspect-[264/180] w-full flex-col items-center justify-center gap-2 rounded-5",
                    "outline-dashed outline-1 -outline-offset-1 outline-on-surface transition-outline hover:outline-on-primary focus:outline-on-primary",
                    "max-lg:aspect-auto max-lg:h-52 max-lg:w-full max-sm:h-36",
                  )}
                >
                  <IconPlus
                    className={
                      "size-6 stroke-on-surface transition-colors group-hover:stroke-on-primary group-focus:stroke-on-primary"
                    }
                  />
                  <span
                    className={
                      "text-on-surface transition-colors group-hover:text-on-primary group-focus:text-on-primary max-lg:text-sm/4"
                    }
                  >
                    {t("Actions.add-profile")}
                  </span>
                </button>
              </li>
            ) : null}
          </ul>
        </nav>
      </div>
      <div className="px-3 max-sm:px-0">
        <h3 className={"mb-4 w-full px-3 text-[1.125rem] font-semibold max-sm:p-0 md:hidden"}>{t("kids-profiles")}</h3>
        <button
          type={"button"}
          ref={buttonRef}
          aria-label={t("Actions.go-to-kids-platform")}
          onClick={() => setTab(ProfileTabs.KID_PROFILES)}
          className={
            "flex w-full flex-col items-center gap-0.5 rounded-5 bg-gradient-to-r from-[#8FD2FB] to-[#0076BD] py-4 font-medium max-lg:text-sm/4"
          }
        >
          <img src={"/images/skids.png"} alt={"skids"} className={"size-20"} />
          {t("Actions.go-to-kids-platform")}
        </button>
      </div>
    </div>
  );
};

export default ChooseProfile;
